import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Select } from '../Controls';
import { imageUrl } from '../services/helpers';
import { bookSpreads, isClosed, sectionOnPage, spreadSides, unitIndexOf } from './book';
import { useWideScreen } from './useWideScreen';
import { useOpenImage } from './viewerContext';
import { loadImages } from './loadImages';
import { BackFace, BookNav, PageButton, FrontFace, Leaf, PageImage, PaperBack, Side, Spread, TextLink } from './styled';

/** A horizontal swipe at least this long turns the page. */
const SWIPE_PX = 50;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const pageParam = (page) => (page.number === null ? 'cover' : String(page.number));

const pageNumberOf = (param) => (param === null || param === 'cover' ? null : Number(param));

/** A page as it lies in the booklet; tapping it opens the photo full screen to zoom. */
const PageView = ({ page }) => {
  const openImage = useOpenImage();
  const src = imageUrl(page.image);
  const alt = `Rulebook ${page.label.toLowerCase()}`;
  return (
    <PageButton type="button" onClick={() => openImage(src, alt)} aria-label={`Zoom into ${page.label.toLowerCase()}`}>
      <PageImage src={src} width={page.width} height={page.height} alt={alt} />
    </PageButton>
  );
};

/**
 * A side with no page: invisible when the booklet lies open, plain paper on the back of a turning page. `sizer` is
 * any page, used only to take up the same space (every page has the same shape).
 */
const BlankPage = ({ sizer, paper }) => {
  const image = (
    <PageImage src={imageUrl(sizer.image)} width={sizer.width} height={sizer.height} alt="" aria-hidden="true" $blank />
  );
  return paper ? <PaperBack>{image}</PaperBack> : image;
};

const pageShape = PropTypes.shape({
  image: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  label: PropTypes.string,
});

const unitImages = (unit) => (unit || []).map(page => imageUrl(page.image));
PageView.propTypes = { page: pageShape.isRequired };
BlankPage.propTypes = { sizer: pageShape.isRequired, paper: PropTypes.bool };

const Face = ({ page, sizer, paper }) => (page ? <PageView page={page} /> : <BlankPage sizer={sizer} paper={paper} />);
Face.propTypes = { page: pageShape, sizer: pageShape.isRequired, paper: PropTypes.bool };

/**
 * The photographed booklet: one page at a time on a phone, two-page spreads on a wide screen, with the cover and
 * page 1 on the right as in the real booklet. Pages turn over like paper unless the device asks for reduced motion.
 * Arrow keys, swipes, the buttons and the page picker all turn pages.
 */
const RulesBook = ({ pages, sections }) => {
  const wide = useWideScreen();
  const [params, setParams] = useSearchParams();
  const [turn, setTurn] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const touchX = useRef(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const units = wide ? bookSpreads(pages) : pages.map(page => [page]);
  const index = unitIndexOf(units, pageNumberOf(params.get('page')));
  const current = units[index] || [];

  const show = useCallback((next) => {
    setParams({ page: pageParam(units[next][0]) }, { replace: true });
  }, [units, setParams]);

  const goTo = useCallback((requested) => {
    const next = Math.min(Math.max(requested, 0), units.length - 1);
    if (next === index || turn || preparing) {
      return;
    }
    if (window.matchMedia(REDUCED_MOTION).matches) {
      show(next);
      return;
    }
    // Turn only once the next pages are ready: a page that arrives mid-turn leaves a gap and then pops in.
    setPreparing(true);
    loadImages(unitImages(units[next])).then(() => {
      if (mounted.current) {
        setPreparing(false);
        setTurn({ to: next, forward: next > index });
      }
    });
  }, [units, index, turn, preparing, show]);

  // Load the pages on either side while this one is read, so most turns start at once.
  useEffect(() => {
    loadImages([...unitImages(units[index - 1]), ...unitImages(units[index + 1])]);
  }, [units, index]);

  // The faces animate too, and their animationend events bubble up here; only the page's own turn finishes it.
  const finishTurn = (event) => {
    if (event.target === event.currentTarget) {
      show(turn.to);
      setTurn(null);
    }
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'ArrowLeft') goTo(index - 1);
      if (event.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, index]);

  if (!current.length) {
    return null;
  }

  const sizer = pages[0];
  const target = turn ? units[turn.to] : null;

  const onTouchEnd = (event) => {
    const dx = event.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) >= SWIPE_PX) {
      goTo(dx < 0 ? index + 1 : index - 1);
    }
  };

  let sides;
  if (wide) {
    // Forward: the right page turns over onto the left, uncovering the next right page. Back: the mirror image.
    const now = spreadSides(current);
    const next = target && spreadSides(target);
    const forward = turn && turn.forward;
    // The left side lying open: the spread being uncovered when turning back, else the current one.
    const underLeft = turn && !forward ? target : current;
    sides = [
      <Side key="left" $side="left">
        <Face page={turn && !forward ? next.left : now.left} sizer={sizer} paper={!isClosed(underLeft)} />
        {turn && !forward && (
          <Leaf $edge="right" onAnimationEnd={finishTurn}>
            <FrontFace><Face page={now.left} sizer={sizer} paper /></FrontFace>
            <BackFace $side="right"><Face page={next.right} sizer={sizer} paper /></BackFace>
          </Leaf>
        )}
      </Side>,
      <Side key="right" $side="right">
        <Face page={forward ? next.right : now.right} sizer={sizer} />
        {forward && (
          <Leaf $edge="left" onAnimationEnd={finishTurn}>
            <FrontFace><Face page={now.right} sizer={sizer} paper /></FrontFace>
            <BackFace $side="left"><Face page={next.left} sizer={sizer} paper /></BackFace>
          </Leaf>
        )}
      </Side>,
    ];
  } else {
    // One page: going forward it turns away to show the next; going back the previous one turns in over it.
    const forward = turn && turn.forward;
    sides = [
      <Side key="page">
        <PageView page={forward ? target[0] : current[0]} />
        {turn && (
          <Leaf $edge="left" $in={!forward} onAnimationEnd={finishTurn}>
            <FrontFace $in={!forward}><PageView page={forward ? current[0] : target[0]} /></FrontFace>
          </Leaf>
        )}
      </Side>,
    ];
  }

  const shownPages = turn ? target : current;
  const textSection = shownPages.map(page => page.number !== null && sectionOnPage(sections, page.number)).find(Boolean);

  return (
    <>
      <BookNav>
        <Button onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous page">‹</Button>
        <Select
          aria-label="Go to page"
          value={pageParam(shownPages[0])}
          onChange={event => goTo(unitIndexOf(units, pageNumberOf(event.target.value)))}
        >
          {pages.map(page => <option key={page.id} value={pageParam(page)}>{page.label}</option>)}
        </Select>
        <Button onClick={() => goTo(index + 1)} disabled={index === units.length - 1} aria-label="Next page">›</Button>
      </BookNav>

      <Spread
        $pages={wide ? 2 : 1}
        $width={sizer.width}
        $height={sizer.height}
        onTouchStart={event => { touchX.current = event.touches[0].clientX; }}
        onTouchEnd={onTouchEnd}
      >
        {sides}
      </Spread>

      {textSection && (
        <TextLink>
          <Link to={`/rules/text?page=${textSection.first_page}#rule-${textSection.position}`}>
            Read “{textSection.heading}” as text
          </Link>
        </TextLink>
      )}
    </>
  );
};

RulesBook.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default RulesBook;
